import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Brain, Utensils, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navigation from '../components/Navigation';
import CrisisDetection from '../components/CrisisDetection';
import { Toaster } from 'sonner';
import MentalHealthTab from '../components/ai-features/MentalHealthTab';
import NutritionTab from '../components/ai-features/NutritionTab';
import HeartMonitoringTab from '../components/ai-features/HeartMonitoringTab';
import SkinScannerTab from '../components/ai-features/SkinScannerTab';
import SymptomCheckerTab from '../components/ai-features/SymptomCheckerTab';
import HealthChatTab from '../components/ai-features/HealthChatTab';
import VoiceAssistantTab from '../components/ai-features/VoiceAssistantTab';
import { ResultDisplays } from '../components/ai-features/ResultDisplays';

const AIHealthFeatures = ({ user, setUser }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('mental-health');
  const [result, setResult] = useState(null);
  const [showCrisisAlert, setShowCrisisAlert] = useState(false);
  const [crisisMessage, setCrisisMessage] = useState(null);

  const tabs = [
    { id: 'mental-health', icon: <Brain className="w-5 h-5" />, label: t('aiFeatures.mentalHealth') },
    { id: 'nutrition', icon: <Utensils className="w-5 h-5" />, label: t('aiFeatures.nutrition') },
    { id: 'skin-scanner', icon: <span className="text-lg">&#128300;</span>, label: t('aiFeatures.skinScanner') },
    { id: 'heart-monitor', icon: <span className="text-lg">&#10084;&#65039;</span>, label: t('aiFeatures.heartMonitor') },
    { id: 'symptom-checker', icon: <span className="text-lg">&#129658;</span>, label: t('aiFeatures.symptomChecker') },
    { id: 'health-chat', icon: <span className="text-lg">&#128172;</span>, label: t('aiFeatures.healthChat') },
    { id: 'voice-assistant', icon: <span className="text-lg">&#127897;&#65039;</span>, label: t('aiFeatures.voiceAssistant') },
  ];

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setResult(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-teal-50">
      <Navigation user={user} setUser={setUser} />
      <Toaster position="top-right" richColors />

      <div className="container mx-auto px-4 pt-24 pb-6 sm:pb-8">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-4xl font-bold text-gray-800 mb-2">{t('aiFeatures.title')}</h1>
          <p className="text-gray-600 mb-8">{t('aiFeatures.subtitle')}</p>

          {/* Tab Selection */}
          <div className="flex flex-wrap gap-3 mb-8" data-testid="ai-features-tabs">
            {tabs.map(tab => (
              <Button key={tab.id} onClick={() => handleTabChange(tab.id)} variant={activeTab === tab.id ? 'default' : 'outline'} className="flex items-center gap-2">
                {tab.icon} {tab.label}
              </Button>
            ))}
            <Button onClick={() => window.location.href = '/ai-prescription'} variant="outline" className="flex items-center gap-2 border-blue-500 text-blue-600 hover:bg-blue-50">
              <FileText className="w-5 h-5" /> {t('nav.aiPrescription')}
            </Button>
          </div>

          {/* Tab Content */}
          {activeTab === 'mental-health' && (
            <MentalHealthTab user={user} setResult={setResult} setCrisisMessage={setCrisisMessage} setShowCrisisAlert={setShowCrisisAlert} />
          )}
          {activeTab === 'nutrition' && <NutritionTab user={user} setResult={setResult} />}
          {activeTab === 'skin-scanner' && <SkinScannerTab user={user} result={result} setResult={setResult} />}
          {activeTab === 'heart-monitor' && <HeartMonitoringTab user={user} setResult={setResult} />}
          {activeTab === 'symptom-checker' && <SymptomCheckerTab user={user} setResult={setResult} />}
          {activeTab === 'health-chat' && <HealthChatTab />}
          {activeTab === 'voice-assistant' && <VoiceAssistantTab user={user} />}

          {/* Results Display */}
          {result && <ResultDisplays result={result} />}

          {/* Crisis Alert */}
          {showCrisisAlert && crisisMessage && (
            <CrisisDetection message={crisisMessage} onClose={() => setShowCrisisAlert(false)} />
          )}
        </div>
      </div>
    </div>
  );
};

export default AIHealthFeatures;
